import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { studioApi } from '../lib/api';
import { toast } from 'sonner';
import { AlertCircle, Plus, Pencil, Trash2, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const TIPI = ['Pagamento', 'Integrazione ente', 'Soprintendenza', 'Catasto', 'Documenti', 'Sopralluogo', 'Altro'];
const STATI = ['Da fare', 'In corso', 'Completata', 'Bloccata'];
const PRIO = ['Alta', 'Media', 'Bassa'];
const STATO_COLORS = { 'Da fare': 'bg-gray-100 text-gray-700', 'In corso': 'bg-yellow-100 text-yellow-800', Completata: 'bg-emerald-100 text-emerald-800', Bloccata: 'bg-red-100 text-red-800' };
const PRIO_COLORS = { Alta: 'bg-red-100 text-red-700', Media: 'bg-amber-100 text-amber-700', Bassa: 'bg-gray-100 text-gray-700' };
const empty = { commessa_id: '', tipo: 'Documenti', descrizione: '', data_scadenza: '', priorita: 'Media', stato: 'Da fare' };

export default function StudioScadenzePage() {
  const [items, setItems] = useState([]);
  const [commesse, setCommesse] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const [s, c] = await Promise.all([studioApi.getScadenze(), studioApi.getCommesse()]);
      setItems(s.data); setCommesse(c.data);
    } catch { toast.error('Errore'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(empty); setEditingId(null); setOpen(true); };
  const openEdit = (s) => { setForm({ ...empty, ...s }); setEditingId(s.id); setOpen(true); };
  const save = async () => {
    if (!form.commessa_id || !form.descrizione || !form.data_scadenza) { toast.error('Compila tutti i campi obbligatori'); return; }
    try {
      if (editingId) { await studioApi.updateScadenza(editingId, form); toast.success('Aggiornata'); }
      else { await studioApi.createScadenza(form); toast.success('Creata'); }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  const remove = async (s) => {
    if (!window.confirm('Eliminare scadenza?')) return;
    try { await studioApi.deleteScadenza(s.id); toast.success('Eliminata'); load(); } catch { toast.error('Errore'); }
  };
  const complete = async (s) => {
    try { await studioApi.updateScadenza(s.id, { stato: 'Completata' }); toast.success('Completata'); load(); } catch { toast.error('Errore'); }
  };

  const sorted = [...items].sort((a, b) => {
    if (a.stato === 'Completata' && b.stato !== 'Completata') return 1;
    if (b.stato === 'Completata' && a.stato !== 'Completata') return -1;
    return (a.giorni_residui ?? 9999) - (b.giorni_residui ?? 9999);
  });

  return (
    <div className="space-y-4 p-4 lg:p-6" data-testid="studio-scadenze-page">
      <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
        <div>
          <div className="flex items-center gap-2"><AlertCircle className="w-6 h-6 text-indigo-600" /><h1 className="text-2xl font-bold">Scadenze</h1><Badge>{items.length}</Badge></div>
          <p className="text-sm text-gray-600 mt-1">Ordinate per giorni residui (scadute in cima)</p>
        </div>
        <Button onClick={openCreate} data-testid="new-scadenza-btn"><Plus className="w-4 h-4 mr-1" /> Nuova scadenza</Button>
      </motion.div>

      {loading ? <p className="text-gray-500 italic">Caricamento...</p> : (
        <div className="bg-white border rounded-lg overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-600 uppercase"><tr>
              <th className="text-left p-2.5">Scadenza</th><th className="text-center p-2.5">Giorni</th>
              <th className="text-left p-2.5">Commessa</th><th className="text-left p-2.5">Cliente</th>
              <th className="text-left p-2.5">Tipo</th><th className="text-left p-2.5">Descrizione</th>
              <th className="text-left p-2.5">Prio</th><th className="text-left p-2.5">Stato</th><th></th>
            </tr></thead>
            <tbody>
              {sorted.map(s => (
                <tr key={s.id} className={`border-t hover:bg-gray-50 ${s.giorni_residui < 0 && s.stato !== 'Completata' ? 'bg-red-50' : ''}`}>
                  <td className="p-2.5 font-mono text-xs">{s.data_scadenza}</td>
                  <td className="p-2.5 text-center">
                    {s.giorni_residui != null && (
                      <span className={`font-semibold ${s.giorni_residui < 0 ? 'text-red-600' : s.giorni_residui <= 7 ? 'text-amber-600' : 'text-gray-600'}`}>
                        {s.giorni_residui < 0 ? `${Math.abs(s.giorni_residui)}g fa` : `${s.giorni_residui}g`}
                      </span>
                    )}
                  </td>
                  <td className="p-2.5 font-mono text-xs text-indigo-700">{s.commessa_codice || '-'}</td>
                  <td className="p-2.5">{s.cliente_nome || '-'}</td>
                  <td className="p-2.5"><Badge variant="outline" className="text-xs">{s.tipo}</Badge></td>
                  <td className="p-2.5 max-w-xs truncate">{s.descrizione}</td>
                  <td className="p-2.5"><Badge className={`text-xs ${PRIO_COLORS[s.priorita] || ''}`}>{s.priorita}</Badge></td>
                  <td className="p-2.5"><Badge className={`text-xs ${STATO_COLORS[s.stato] || ''}`}>{s.stato}</Badge></td>
                  <td className="p-2.5 text-right">
                    {s.stato !== 'Completata' && <Button size="icon" variant="ghost" title="Completa" onClick={() => complete(s)}><CheckCircle2 className="w-4 h-4 text-emerald-600" /></Button>}
                    <Button size="icon" variant="ghost" onClick={() => openEdit(s)}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(s)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                  </td>
                </tr>
              ))}
              {sorted.length === 0 && <tr><td colSpan="9" className="text-center text-gray-500 italic py-10">Nessuna scadenza.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{editingId ? 'Modifica scadenza' : 'Nuova scadenza'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2"><Label>Commessa *</Label>
              <Select value={form.commessa_id || '__none__'} onValueChange={(v) => setForm({ ...form, commessa_id: v === '__none__' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent><SelectItem value="__none__">--</SelectItem>{commesse.map(c => <SelectItem key={c.id} value={c.id}>{c.codice} · {c.cliente_nome}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TIPI.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Data scadenza *</Label><Input type="date" value={form.data_scadenza} onChange={(e) => setForm({ ...form, data_scadenza: e.target.value })} /></div>
            <div className="md:col-span-2"><Label>Descrizione *</Label><Input value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} /></div>
            <div><Label>Priorità</Label>
              <Select value={form.priorita} onValueChange={(v) => setForm({ ...form, priorita: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PRIO.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Stato</Label>
              <Select value={form.stato} onValueChange={(v) => setForm({ ...form, stato: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{STATI.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
